// src/pages/TenantManagement/TenantList.tsx
import React from 'react';
import { Badge, Button } from 'reactstrap';
import { useLocation } from 'react-router-dom';
import { Tenant } from './types';
import { PageActionPermission } from 'services/pageActionPermission/PageActionPermission';
import DataTableShell, { getSttNumber } from 'Components/Common/DataTableShell';
import { createScreenText } from 'helpers/screenText';
const st = createScreenText('tenant_management');

interface TenantListProps {
  tenants: Tenant[];
  loading: boolean;
  onEdit: (tenant: Tenant) => void;
  onDelete: (tenant: Tenant) => void;
  page: number;
  pageSize: number;
}

// Tập action-code cua cot "Thao tac" — dung de an han ca cot khi role hien tai khong co quyen voi
// bat ky action nao trong cot.
const TENANT_ACTION_COLUMN_CODES = ["button-edit", "button-delete"];

const TenantList: React.FC<TenantListProps> = ({
  tenants,
  loading,
  onEdit,
  onDelete,
  page,
  pageSize,
}) => {
  const location = useLocation();
  const canShowActionColumn = PageActionPermission.canDisplayAny(
    location.pathname,
    TENANT_ACTION_COLUMN_CODES,
  );

  if (loading) {
    return (
      <div className="text-center py-5">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">{st('loading_text')}</span>
        </div>
        <p className="text-muted mt-2">{st('dang_tai_du_lieu_text')}</p>
      </div>
    );
  }

  if (tenants.length === 0) {
    return (
      <div className="text-center py-5">
        <i className="ri-building-line display-4 text-muted"></i>
        <h5 className="mt-3">{st('chua_co_don_vi_nao_title')}</h5>
        <p className="text-muted">{st('tao_don_vi_dau_tien_de_text')}</p>
      </div>
    );
  }

    return (
    <DataTableShell
      headerRow={
        <tr>
          <th className="text-center" scope="col" style={{ width: 60 }}>{st('stt_column')}</th>
          <th scope="col" style={{ width: '12%' }}>
            {st('ma_column')}
          </th>
          <th scope="col">
            {st('ten_don_vi_column')}
          </th>
          <th scope="col" style={{ width: '15%' }}>
            {st('loai_column')}
          </th>
          <th scope="col" style={{ width: '15%' }}>
            {st('quan_ly_text')}
          </th>
          <th scope="col" style={{ width: '12%' }}>
            {st('so_nhan_vien_column')}
          </th>
          <th scope="col" style={{ width: '12%' }}>
            {st('trang_thai_label_2')}
          </th>
          {canShowActionColumn && (
          <th scope="col" style={{ width: '10%' }}>
            {st('thao_tac_column')}
          </th>
          )}
        </tr>
      }
    >
          {tenants.map((tenant, rowIndex) => (
            <tr key={tenant.id}>
              <td className="text-center">{getSttNumber(page, pageSize, rowIndex)}</td>
              <td>
                <span className="fs-12">
                  {tenant.code}
                </span>
              </td>
              <td>
                <div className="d-flex align-items-center">
                  <div className="flex-shrink-0 avatar-xs me-2">
                    <div className="avatar-title bg-soft-info text-info rounded-circle fs-13">
                      <i className="ri-building-line"></i>
                    </div>
                  </div>
                  <div className="flex-grow-1">
                    <h6 className="fs-14 mb-0">{tenant.name}</h6>
                    {tenant.description && (
                      <small className="text-muted">{tenant.description}</small>
                    )}
                  </div>
                </div>
              </td>
                            <td>
                <Badge color={tenant.type === 1 ? 'primary' : 'info'} className={tenant.type === 1 ? 'badge-soft-primary' : 'badge-soft-info'}>
                  <i className={tenant.type === 1 ? 'ri-building-4-line me-1' : 'ri-home-office-line me-1'}></i>
                  {tenant.typeName}
                </Badge>
              </td>
                            <td>
                {tenant.managerName ? (
                  <div className="d-flex align-items-center">
                    <div className="flex-shrink-0 me-2">
                      {tenant.managerAvatar ? (
                        <img 
                          src={tenant.managerAvatar} 
                          alt={tenant.managerName}
                          className="avatar-sm rounded-circle"
                          style={{ 
                            width: '32px', 
                            height: '32px', 
                            objectFit: 'cover',
                            objectPosition: 'center'
                          }}
                        />
                      ) : (
                        <div className="avatar-sm rounded-circle d-flex align-items-center justify-content-center bg-soft-secondary text-secondary fw-medium">
                          {tenant.managerName.charAt(0).toUpperCase()}
                        </div>
                      )}
                    </div>
                    <div className="flex-grow-1">
                      <h6 className="fs-14 mb-0 text-dark">{tenant.managerName}</h6>
                      {tenant.managerEmail && (
                        <small className="text-muted">{tenant.managerEmail}</small>
                      )}
                    </div>
                  </div>
                ) : (
                  <span className="text-muted">--</span>
                )}
              </td>
              <td>
                <span className="fs-12">
                  <i className="ri-user-line me-1"></i>
                  {tenant.employeeCount}
                </span>
              </td>
              <td>
                {tenant.status === 1 ? (
                  <Badge color="success" className="badge-soft-success">
                    <i className="ri-checkbox-circle-line me-1"></i>
                    {tenant.statusName}
                  </Badge>
                ) : (
                  <Badge color="danger" className="badge-soft-danger">
                    <i className="ri-close-circle-line me-1"></i>
                    {tenant.statusName || st('ngung_hoat_dong_text')}
                  </Badge>
                )}
              </td>
              {canShowActionColumn && (
              <td>
                <div className="d-flex justify-content-center gap-1">
                  {PageActionPermission.canDisplay(location.pathname, "button-edit") && (
                    <Button
                      color="secondary"
                      outline
                      size="sm"
                      className="btn-icon"
                      title={st('chinh_sua_title')}
                      data-action-code="button-edit"
                      data-action-name="Chỉnh sửa đơn vị"
                      onClick={() => onEdit(tenant)}
                    >
                      <i className="ri-pencil-line"></i>
                    </Button>
                  )}
                  {PageActionPermission.canDisplay(location.pathname, "button-delete") && (
                    <Button
                      color="danger"
                      outline
                      size="sm"
                      className="btn-icon"
                      title={st('xoa_title')}
                      data-action-code="button-delete"
                      data-action-name="Xóa đơn vị"
                      onClick={() => onDelete(tenant)}
                    >
                      <i className="ri-delete-bin-line"></i>
                    </Button>
                  )}
                </div>
              </td>
              )}
            </tr>
          ))}
    </DataTableShell>
  );
};

export default TenantList;